/**
 * @file asset.ts
 * @description
 * Utility for building correct URLs to static assets under the project's `public/` directory.
 *
 * This function ensures assets resolve properly in both:
 * - **Development:** where assets are served from the root `/`
 * - **Static builds / production:** where assets may be served from a subpath
 *   defined by Vite's `base` config (exposed via `import.meta.env.BASE_URL`)
 *
 * @example
 * // Example: building a path to a data file
 * const url = asset("data/congress118/bills/hconres3.json");
 * // => "/data/congress118/bills/hconres3.json" (dev)
 * // => "/myapp/data/congress118/bills/hconres3.json" (if base="/myapp/")
 *
 * @example
 * // Example: using with fetch
 * const response = await fetch(asset("data/congress118/bills/hconres3.json"));
 * const data = await response.json();
 */

/**
 * Build an absolute URL to a static asset that respects Vite’s `base` path.
 *
 * @param path - Relative path to an asset under the `public/` directory
 * @returns A fully-qualified URL string for use with fetch, img src, etc.
 */
export const asset = (path: string): string => {
  const base = (import.meta.env.BASE_URL || '/').replace(/\/$/, '');
  const cleanPath = String(path).replace(/^\//, '');
  return `${base}/${cleanPath}`;
};
